import { Causes, MetroRegion } from "@fieldday/fielddayportal-model"
import CheckBoxIcon from "@mui/icons-material/CheckBox"
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank"
import ClearIcon from "@mui/icons-material/Clear"
import {
  Autocomplete, Box, Button, Checkbox, Chip, ChipProps, Divider,
  FormControl, FormLabel, Grid, Popper, Skeleton, Stack, TextField, Typography, useMediaQuery, useTheme
} from "@mui/material"
import qs from "qs"
import { ChangeEvent, Fragment, SyntheticEvent, useEffect, useState } from "react"
import { Helmet } from "react-helmet"
import { Link as RouterLink } from "react-router-dom"
import ContentContainer from "../../components/Global/ContentContainer"
import EmptyPageCallToAction from "../../components/Global/EmptyPageCallToAction"
import Header from "../../components/Global/Header"
import { RegionFilterPopover, regionFilterLabel } from "../../components/Global/RegionSelector"
import { useAuth } from "../../hooks/useAuth"
import { NPO } from "../../models/Npo"
import useFormStyles from "../../styles/formStyles"
import useGlobalStyles from "../../styles/globalStyles"
import { appBarHeight } from "../../styles/navBarStyles"
import useStyles from "../../styles/useStyles"
import { CTAIconSrc } from "../../util/icons"
import { queryParamStringArray, setQueryParams } from "../../util/urlManipulation"
import { useAPI } from "../../util/useAPI"

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

type NonprofitDictionary = {
  [firstChar: string]: Required<NPO>[]
}

export type NpoQueryParams = {
  causes: string[],
  namelike?: string,
  region?: MetroRegion,
}

function CauseChips({ causeAreas, removeCause, clearCauses }: {
  causeAreas: string[],
  removeCause: (cause: string) => void,
  clearCauses: () => void,
}) {
  const styles = useStyles()

  const chipDefaults = {
    deleteIcon: <ClearIcon />,
    size: "small",
    color: "info",
  } as ChipProps

  return (<>
    {causeAreas.map(causeArea => {
      return <Chip {...chipDefaults} label={causeArea} key={causeArea}
        onDelete={() => { removeCause(causeArea) }}
      />
    })}
    {causeAreas.length > 0 &&
      <Button onClick={clearCauses} size="small" className={styles.filterClearButton}>Clear all</Button>
    }
  </>)
}

export default function ListNonProfits() {
  const FieldDayAPI = useAPI()
  const { trackVisitInCIO } = useAuth()
  const query = qs.parse(location.search, { ignoreQueryPrefix: true }) as NpoQueryParams
  const [npoList, setNpoList] = useState<Required<NPO>[] | undefined>(undefined)
  const [selectedNpo, setSelectedNpo] = useState<Required<NPO> | null>(null)
  const styles = useStyles()
  const globalStyles = useGlobalStyles()
  const formStyles = useFormStyles()
  const theme = useTheme()
  const [nonprofitDictionary, setNonprofitDictionary] = useState<NonprofitDictionary>({})
  const dictionaryEntries = Object.entries(nonprofitDictionary)
  const [queryFilters, setQueryFilters] = useState<NpoQueryParams>(query)
  const lgView = useMediaQuery(theme.breakpoints.down('lg'))
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'))
  const intermediateView = useMediaQuery(theme.breakpoints.between('sm', 'lg'))

  useEffect(() => {
    trackVisitInCIO("List nonprofits")
  }, [])

  useEffect(() => {
    const abortController = new AbortController()
    if (queryFilters.region === undefined) return setNpoList(undefined)
    FieldDayAPI.listNpos({ causes: queryFilters.causes, namelike: queryFilters.namelike, region: queryFilters.region },
      abortController
    ).then(resp => {
      setNpoList(resp.data.nonProfitOrgs as Required<NPO>[])
    })
    setQueryParams(queryFilters)
    return () => {
      abortController.abort()
    }
  }, [queryFilters])

  useEffect(() => {
    const dictionary = createNonprofitDictionary(npoList)
    setNonprofitDictionary(dictionary)
  }, [npoList])

  const createNonprofitDictionary = (npos: Required<NPO>[] | undefined): NonprofitDictionary => {
    if(!npos) return {}
    const dictionary: NonprofitDictionary = {}
    npos.sort((a, b) => a.name > b.name ? 1 : -1)
    for (const npo of npos) {
      const firstChar = npo.name[0].toLocaleUpperCase()
      if (dictionary[firstChar]) {
        dictionary[firstChar].push(npo)
      } else {
        dictionary[firstChar] = [npo]
      }
    }
    return dictionary
  }

  const handleChangeNpo = (_event: SyntheticEvent<Element, Event>, value: Required<NPO> | null) => {
    setSelectedNpo(value)
  }

  const onFilterChange = (qp: NpoQueryParams, reset: boolean = false) => {
    if (reset) {
      setQueryFilters({
        causes: [],
        namelike: undefined,
        region: undefined
      })
    } else {
      const newQueryFilters: NpoQueryParams = { ...queryFilters, ...qp }
      if (qs.stringify(newQueryFilters) !== qs.stringify(queryFilters)) setQueryFilters(newQueryFilters)
    }
  }

  const NpoPicker = (
    <FormControl className={styles.formControl}>
      <FormLabel component="legend" className={formStyles.formLabel}>Nonprofits</FormLabel>
      <Autocomplete
        onChange={handleChangeNpo}
        value={selectedNpo}
        onInputChange={(_event, newInputValue) => {
          onFilterChange({ ...queryFilters, namelike: newInputValue })
        }}
        popupIcon={false}
        PopperComponent={CustomPopper}
        clearOnBlur={false}
        options={npoList ?? []}
        getOptionLabel={(n: Required<NPO>) => n.name}
        isOptionEqualToValue={(_option, _value) => true}
        id="nonProfitOrgs"
        renderInput={params => (
          <TextField {...params} variant="standard" placeholder="Search ..." />
        )}
      />
    </FormControl>
  )

  const displayNpoList = () => {
    if (!npoList) return <NpoListSkeleton />
    if (npoList?.length === 0) {
      return (
        <EmptyPageCallToAction
          header={`Browsing nonprofits near ${regionFilterLabel('npoList', queryFilters.region)}`}
          description='Field Day has begun partnering with select nonprofit partners to support this region,
        please contact us to discuss setting up an Impact campaign.'
          iconSrc={CTAIconSrc.Nonprofit}
          callToActionLink="https://www.fieldday.com/companies/impact-campaigns"
          callToActionText="Impact campaigns"
          secondaryCallToActionLink="https://www.fieldday.com/contact-sales"
          secondaryCallToActionText="Contact us"
          external
        />
      )
    }
    return (
      <>
        {dictionaryEntries.map(([char, npos], dictIndex) => (
          <Fragment key={`npos|${char}`}>
            <Typography variant="h5" sx={{ color: theme.palette.primary.dark }} mb={3} mt={2}>
              {char}
            </Typography>
            {npos.map((npo, npoIndex) => (
              <Fragment key={npo.id}>
                <li style={{ listStyle: "none" }}>
                  <Stack direction={mobileView ? "column" : "row"}
                    justifyContent="space-between" alignItems={mobileView ? 'start' : "center"}>
                    <Typography
                      variant="subhead3B"
                      component={RouterLink} to={`/nonprofits/${npo.handle}`}
                      sx={{ color: `${theme.palette.primary.dark} !important` }}
                    >
                      {npo.name}
                    </Typography>
                    <Stack direction="row" alignItems="center" mt={mobileView ? .5 : 0}>
                      {npo.primaryCause &&
                        <Chip color="info" size="small" label={npo.primaryCause} sx={{ marginLeft: mobileView ? 0 : 1.5 }} />}
                      {npo.secondaryCause && <Chip color="info" size="small" label={npo.secondaryCause} />}
                    </Stack>
                  </Stack>
                </li>
                {npoIndex !== npos.length - 1 && <Divider sx={{ my: 1, borderColor: "rgba(193, 197, 200, .5)" }} />}
              </Fragment>
            ))}
            {dictIndex !== dictionaryEntries.length - 1 && <Divider sx={{ mt: 5.625 }} />}
          </Fragment>
        ))}
      </>
    )
  }

  return (
    <>
      <Helmet title="Browsing Nonprofits" />
      <Header primaryHeader="Nonprofits" withDivider />
      <ContentContainer>
        <Grid container mb={4} className={globalStyles.heroContent}>
          <Grid item xs={12} ml={mobileView ? 0 : 2} sx={{ position: lgView ? "static" : "sticky", top: appBarHeight }}>
            <RegionFilterPopover
              filterPageLoc="npoList"
              flexDirection='row'
              queryParams={queryFilters}
              onFilterChange={onFilterChange}
            />
          </Grid>
          <Grid item xs={12} lg={3} sx={{ marginLeft: 0 }}>
            <Box style={{ position: lgView ? "static" : "sticky", top: `calc(${appBarHeight} + 1.5rem)` }}>
              {NpoPicker}
              <CausePicker queryParams={queryFilters} onFilterChange={onFilterChange} />
            </Box>
          </Grid>

          {/* Spacer element */}
          <Grid item xs={0} lg={.5} />
          <Grid item xs={12} lg={8.5} sx={{ paddingLeft: intermediateView ? 2 : 0 }} component="ul">
            {displayNpoList()}
          </Grid>
        </Grid>
      </ContentContainer>
    </>
  )
}

export function CausePicker<ParamsType extends { causes: string[] | string }>({ queryParams, onFilterChange, styleOverride }: {
  queryParams: ParamsType,
  onFilterChange: (qp: ParamsType) => void,
  styleOverride?: { width?: string }
}) {
  const styles = useStyles()
  const formStyles = useFormStyles()

  const causeList = queryParamStringArray(queryParams.causes)

  const removeCause = (cause: string) => {
    const newCauses = causeList.filter(c => c !== cause)
    onFilterChange({ ...queryParams, causes: newCauses })
  }

  const handleChange = (_event: ChangeEvent<unknown>, values: Array<string>) => {
    onFilterChange({ ...queryParams, causes: values })
  }

  return (
    <>
      <FormControl className={styles.formControl} style={{ width: styleOverride?.width ? styleOverride?.width : "" }}>
        <FormLabel className={formStyles.formLabel}>Cause areas</FormLabel>
        <Autocomplete
          onChange={handleChange}
          multiple
          options={Causes}
          value={causeList}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox icon={checkboxIcon} checkedIcon={checkedIcon} checked={selected} />
              {option}
            </li>
          )}
          renderTags={() => null} // hide the chips
          disableClearable // get rid of the X
          renderInput={(params) => (
            <TextField {...params} variant="standard" placeholder="Search ..." />
          )}
        />

      </FormControl>
      <CauseChips
        causeAreas={causeList}
        removeCause={(cause) => { removeCause(cause) }}
        clearCauses={() => onFilterChange({ ...queryParams, causes: []})}
      />
    </>
  )
}

const CustomPopper = (_props: { open: boolean }) => {
  return <Popper open={false} />
}

const NpoListSkeleton = () => {
  const theme = useTheme()
  const intermediateView = useMediaQuery(theme.breakpoints.between('sm', 'lg'))
  return (
    <Grid item xs={12} sx={{ paddingLeft: intermediateView ? 2 : 0 }} component="ul">
      {[1, 2, 3, 4, 5].map(i => {
        return (
          <Fragment key={i} >
            <li style={{ listStyle: "none", width: '100%' }}>
              <Skeleton variant='rectangular' animation='wave' height={50} sx={{ marginBottom: '10px', borderRadius: '5px', width: '100%' }} />
            </li>
          </Fragment>
        )
      })}
    </Grid>
  )
}
